import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  Banknote,
  Boxes,
  Loader2,
  PackagePlus,
  Pencil,
  Percent,
  Scale,
  SlidersHorizontal,
  Warehouse,
} from 'lucide-react';
import clsx from 'clsx';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { DataTable } from '../../../../components/ui/DataTable';
import { StatCard } from '../../../../components/ui/StatCard';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';
import { Field, inputClass } from '../../../../components/ui/Field';
import { useNotice } from '../../../../hooks/useNotice';
import { SupplyFormModal } from './modals/SupplyFormModal';
import { SUPPLY_MOVEMENT, SupplyMovementModal } from './modals/SupplyMovementModal';
import { fetchSupply, saveSupplyPrices } from '../../../../features/supplies/suppliesThunks';
import { clearSupplyDetail } from '../../../../features/supplies/suppliesSlice';
import {
  buildPricesPayload,
  formatMoney,
  formatPercent,
  formatSupplyMoment,
  pricesFormFrom,
  supplyMovementLabel,
} from '../../../../features/supplies/suppliesUtils';
import { selectWarehouses } from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const SUPPLIES_PATH = '/warehouse/supplies';
const MOVEMENTS_PER_PAGE = 20;

/**
 * `/warehouse/supplies/:supplyId`: one supply — how much there is, what it
 * costs, what each client is charged for it, and every movement that explains
 * its balance.
 *
 * The balance is never typed in: it moves with a replenishment, which carries a
 * cost and recalculates the average, or with an adjustment, which carries a
 * reason and leaves the cost alone. Two acts, two buttons.
 */
export const SupplyDetail = () => {
  const { supplyId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail, history, detailLoading, detailError, saving } = useSelector(
    (state) => state.supplies
  );
  const warehouses = useSelector(selectWarehouses);

  /** `'replenish'`, `'adjust'` or `null`. */
  const [movement, setMovement] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [prices, setPrices] = useState({});
  const [pricesError, setPricesError] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage, setHistoryPerPage] = useState(MOVEMENTS_PER_PAGE);
  const setNotice = useNotice('supply-detail-notice');

  const loadSupply = useCallback(() => dispatch(fetchSupply(supplyId)), [dispatch, supplyId]);

  useEffect(() => {
    loadSupply();
    return () => dispatch(clearSupplyDetail());
  }, [loadSupply, dispatch]);

  // The price fields follow what the server keeps: on opening, and after every
  // save, which answers the supply with its exceptions as they were left.
  const savedPrices = detail?.client_prices;
  useEffect(() => {
    setPrices(pricesFormFrom({ client_prices: savedPrices }));
  }, [savedPrices]);


  // The id is compared too: while the next supply loads, the previous one is
  // still in the store and must not be drawn under the new address.
  const isCurrent = Boolean(detail) && String(detail.id) === String(supplyId);

  // A failed reload of the supply on screen leaves it there, under a strip.
  if (detailError && !isCurrent) {
    return (
      <ErrorState title="No se pudo abrir el insumo" description={detailError} onRetry={loadSupply}>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate(SUPPLIES_PATH)}>
          Volver a Insumos
        </Button>
      </ErrorState>
    );
  }

  if (detailLoading || !isCurrent) {
    return (
      <div data-testid="supply-detail-loading" className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-ninja-green animate-spin" />
      </div>
    );
  }

  const supply = detail;
  const hasCost = Number(supply.average_cost) > 0;

  // A price is only set for a client that stores in this warehouse: one that
  // does not is a figure nobody will be able to charge.
  const clients = (
    warehouses.find((warehouse) => warehouse.id === supply.warehouse)?.clients || []
  ).map((contract) => ({ value: contract.company, label: contract.company_name }));

  const handleSavePrices = async () => {
    setPricesError(null);
    const action = await dispatch(
      saveSupplyPrices({ supplyId: supply.id, prices: buildPricesPayload(prices, clients) })
    );
    if (action.meta.requestStatus === 'fulfilled') {
      setNotice({ type: 'success', message: 'Precios por cliente guardados.' });
    } else {
      setPricesError(action.payload);
    }
  };

  const lastPage = Math.max(1, Math.ceil(history.length / historyPerPage));
  const page = Math.min(historyPage, lastPage);
  const pageMovements = history.slice((page - 1) * historyPerPage, page * historyPerPage);

  const historyColumns = [
    {
      header: 'FECHA',
      cell: (row) => (
        <span className="text-text-secondary">{formatSupplyMoment(row.created_at)}</span>
      ),
    },
    {
      // What happened and why: the reference of a purchase, the reason of an
      // adjustment.
      header: 'MOVIMIENTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[280px]">
          <span className="font-medium text-ninja-dark dark:text-white">
            {supplyMovementLabel(row.movement_type)}
          </span>
          {(row.reference || row.notes) && (
            <span
              className="text-xs text-text-muted mt-0.5 truncate"
              title={[row.reference, row.notes].filter(Boolean).join(' · ')}
            >
              {[row.reference, row.notes].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'CANTIDAD',
      cell: (row) => {
        const quantity = Number(row.quantity);
        return (
          <span
            data-testid="supply-movement-quantity"
            className={clsx(
              'font-semibold tabular-nums',
              quantity < 0 ? 'text-danger' : 'text-success'
            )}
          >
            {quantity > 0 ? '+' : ''}
            {formatQuantity(row.quantity)}
          </span>
        );
      },
    },
    {
      header: 'COSTO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="tabular-nums">{formatMoney(row.unit_cost)}</span>
          <span className="text-xs text-text-muted mt-0.5">
            Promedio {formatMoney(row.average_cost_after)}
          </span>
        </div>
      ),
    },
    {
      header: 'SALDO',
      cell: (row) => <span className="tabular-nums">{formatQuantity(row.balance_after)}</span>,
    },
    {
      header: 'QUIÉN',
      cell: (row) => <span className="text-text-secondary">{row.actor_email || 'sistema'}</span>,
    },
  ];

  return (
    <div data-testid="supply-detail-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Insumos" to={SUPPLIES_PATH} current={supply.sku} />

      {detailError && (
        <StaleDataNotice testId="supply-detail-stale-error" error={detailError} onRetry={loadSupply} />
      )}

      <DetailHero
        media={<Archive size={36} />}
        title={supply.name}
        badges={
          <ToneBadge tone={supply.is_active ? 'success' : 'neutral'} testId="supply-status">
            {supply.is_active ? 'Activo' : 'De baja'}
          </ToneBadge>
        }
        meta={
          <>
            <span className="font-mono font-semibold text-ninja-dark dark:text-white">
              {supply.sku}
            </span>
            {supply.barcode && <span className="font-mono">EAN {supply.barcode}</span>}
            <span className="flex items-center gap-1.5">
              <Warehouse size={14} /> {supply.warehouse_name}
            </span>
          </>
        }
        actions={
          <>
            <Button
              variant="primary"
              icon={PackagePlus}
              onClick={() => setMovement(SUPPLY_MOVEMENT.REPLENISH)}
              data-testid="supply-replenish"
            >
              Reponer
            </Button>
            <Button
              variant="ghost"
              icon={SlidersHorizontal}
              onClick={() => setMovement(SUPPLY_MOVEMENT.ADJUST)}
              data-testid="supply-adjust"
            >
              Ajustar
            </Button>
            <Button
              variant="ghost"
              icon={Pencil}
              onClick={() => setIsEditing(true)}
              data-testid="supply-edit"
            >
              Editar
            </Button>
          </>
        }
      />

      <div data-testid="supply-figures" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          title="Saldo"
          value={formatQuantity(supply.quantity)}
          icon={Boxes}
          subtitle="Se mueve con reposiciones y ajustes"
        />
        <StatCard
          title="Costo promedio"
          value={hasCost ? formatMoney(supply.average_cost) : 'Sin costo'}
          icon={Scale}
          subtitle={
            hasCost ? 'Ponderado: lo recalcula cada reposición' : 'Lo fija la primera reposición'
          }
        />
        <StatCard
          title="Valor del stock"
          value={formatMoney(supply.stock_value)}
          icon={Banknote}
          subtitle="Saldo × costo promedio"
        />
        <StatCard
          title="Margen"
          value={formatPercent(supply.margin)}
          icon={Percent}
          tone={supply.margin !== null && supply.margin < 0 ? 'danger' : undefined}
          subtitle={
            supply.margin === null
              ? 'Hace falta un precio y un costo'
              : `Lo que se gana por unidad sobre su costo, cobrando ${formatMoney(supply.default_price)}`
          }
        />
      </div>

      <section
        data-testid="supply-prices"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border"
      >
        <h2 className="text-lg font-bold text-ninja-dark dark:text-white">Precios por cliente</h2>
        <p className="text-sm text-text-secondary mt-1 mb-5">
          Lo que se le cobra a cada cliente por unidad. Vacío usa el precio por defecto (
          {supply.default_price === null ? 'no se cobra' : formatMoney(supply.default_price)}).
        </p>

        {pricesError && (
          <div
            data-testid="supply-prices-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm mb-4"
          >
            {pricesError}
          </div>
        )}

        {clients.length === 0 ? (
          <p data-testid="supply-prices-empty" className="text-sm text-text-muted">
            {supply.warehouse_name} todavía no tiene clientes con contrato activo.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <Field key={client.value} label={client.label} htmlFor={`supply-price-${client.value}`}>
                  <input
                    id={`supply-price-${client.value}`}
                    type="number"
                    min="0"
                    step="any"
                    data-testid={`supply-price-${client.value}`}
                    value={prices[client.value] ?? ''}
                    onChange={(event) =>
                      setPrices({ ...prices, [client.value]: event.target.value })
                    }
                    placeholder="Precio por defecto"
                    disabled={saving}
                    className={inputClass}
                  />
                </Field>
              ))}
            </div>
            <div className="flex justify-end mt-5">
              <Button
                variant="secondary"
                loading={saving}
                loadingLabel="Guardando..."
                onClick={handleSavePrices}
                data-testid="supply-prices-save"
              >
                Guardar precios
              </Button>
            </div>
          </>
        )}
      </section>

      <div className="space-y-3">
        <h2 className="text-lg font-bold text-ninja-dark dark:text-white">Historial</h2>
        <p className="text-sm text-text-secondary">
          Cada movimiento con el saldo y el costo que dejó. No se edita: una corrección es un
          movimiento nuevo.
        </p>
        <DataTable
          columns={historyColumns}
          data={pageMovements}
          totalItems={history.length}
          currentPage={page}
          itemsPerPage={historyPerPage}
          itemLabel="movimientos"
          emptyMessage="Sin movimientos todavía. Repón su stock para que empiece a costear."
          onPageChange={setHistoryPage}
          onRowsPerPageChange={(rowsPerPage) => {
            setHistoryPerPage(rowsPerPage);
            setHistoryPage(1);
          }}
        />
      </div>

      <SupplyMovementModal
        mode={movement}
        supply={supply}
        onClose={() => setMovement(null)}
        onDone={(message) => setNotice({ type: 'success', message })}
      />

      <SupplyFormModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        warehouse={null}
        supply={supply}
        onSaved={(message) => setNotice({ type: 'success', message })}
      />

    </div>
  );
};
